/*
 * Add to cart without leaving the page, and open Horizon's own cart drawer (item 2).
 *
 * **The drawer is the baseline's, not ours.** Horizon already ships `snippets/cart-drawer.liquid`
 * and `assets/cart-drawer.js`, the store is already set to `cart_type: drawer`, and the drawer is
 * already rendered once in `layout/theme.liquid`. Nothing needed building or enabling. The only
 * thing missing was that this build's buy form was a plain POST, which navigates to /cart, so the
 * drawer never got a chance to open.
 *
 * So this module does one job: turn that POST into an AJAX add, and then announce it the way the
 * baseline announces its own adds. `cart-drawer-component` carries `auto-open` and listens on
 * `document` for a `CartLinesUpdateEvent` with `action: 'add'`; `cart-items-component` listens for
 * the same event and re-renders the drawer's contents from it. Dispatching that one event is
 * therefore all it takes to get the slide-in, the line items, the quantities, the subtotal and the
 * checkout button, all of them the baseline's, all already styled to the theme.
 *
 * Deliberately NOT reimplemented here: the drawer, the focus trap, Esc, the backdrop, the scroll
 * lock, the subtotal maths, the quantity steppers inside the drawer. Those are Horizon's and they
 * already work.
 *
 * Opt-in per form via `data-mh-drawer`, which only `mh-product-hero` sets. A form without it keeps
 * the plain POST, and so does every visitor with scripting off: the form still has a real action
 * and a real method, so add to cart never depends on this file.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const SUBMITTING = 'mh-is-adding';

/** The section ids of any cart-items components on the page, so Shopify returns their re-rendered HTML. */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
}

/** Re-read the cart so the resolved event carries real totals rather than the add response alone. */
async function fetchCart() {
  const res = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return res.json();
}

/*
 * The drawer is NOT opened on the click (2026-10-27, D119). An earlier version called the drawer's
 * own `open()` here to save the ~1s Horizon waits on a first add into an empty cart. Recorded at
 * 390px on the live Excel Hana page, that open showed "Your cart is empty", then Horizon's
 * re-render of the drawer section closed it for ~0.7s and its auto-open opened it again with the
 * item: a visible blink. Horizon's own timing (straight away when the cart has items, on the
 * response when it is empty) opens it once. The button shows a spinner meanwhile (mh-is-adding).
 */
async function addToCart(form) {
  const data = new FormData(form);
  const variantId = data.get('id');
  const quantity = Number(data.get('quantity')) || 1;
  if (!variantId) return;

  const button = form.querySelector('[data-mh-atc]');
  const sections = cartSectionIds();

  // Announce the add before the network call, exactly as the baseline product form does, so the
  // drawer can start opening while the request is in flight rather than after it.
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );


  form.classList.add(SUBMITTING);
  if (button) button.disabled = true;

  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: sections.join(','),
      }),
    });
    const payload = await res.json();

    // Shopify reports a rejected add with a `status` field rather than an HTTP error.
    if (payload.status) {
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'mh-buy-form', didError: true },
      });
      // Fall back to the real form post, which surfaces Shopify's own error page rather than
      // leaving the customer with a button that silently did nothing.
      form.submit();
      return;
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: payload.sections,
        source: 'mh-buy-form',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    // Network trouble: hand the customer back to the plain POST rather than swallowing the click.
    form.submit();
  } finally {
    form.classList.remove(SUBMITTING);
    if (button) button.disabled = false;
  }
}

/*
 * One delegated listener. `submit` catches both entry points for free: the in-page ADD TO CART,
 * which is inside the form, and the sticky bar's button, which is outside it but associated by
 * `form="<id>"` and so submits the very same form.
 */
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-mh-drawer')) return;

    // No drawer on the page means no drawer to open; let the normal POST happen.
    if (!document.querySelector('cart-drawer-component')) return;

    event.preventDefault();
    addToCart(form);
  },
  // Capture, so this runs before any other submit handler can act on the form.
  { capture: true }
);

/*
 * Close on an outside click at desktop widths.
 *
 * Below 990px Horizon opens the drawer as a modal dialog and already closes it when the backdrop
 * is clicked, so nothing is needed there. At and above 990px it opens as a NON-modal sidebar that
 * squeezes the page: there is no ::backdrop, and therefore no backdrop handler. This adds the same
 * affordance for that mode so "click outside to close" is true at every width.
 *
 * It calls the baseline drawer's own public `close()` and adds no styling and no state of its own.
 */
document.addEventListener('pointerdown', (event) => {
  const drawer = document.querySelector('cart-drawer-component');
  const themeDrawer = drawer?.closest('theme-drawer');
  const panel = drawer?.closest('dialog');
  if (!themeDrawer || !panel || !panel.open) return;

  // Modal mode already handles this natively.
  if (panel.matches(':modal')) return;

  if (panel.contains(event.target)) return;

  // Ignore the control that opens it, or the click would close and reopen in one gesture.
  if (event.target instanceof Element && event.target.closest('[aria-controls="cart-drawer"], .mh-buy__add, .mh-floating-bar__add, [data-mh-atc]')) return;

  themeDrawer.close?.();
});
